import React, { memo, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Provider, useStore, useDispatch } from 'react-redux';
import { gridReady, updateQueryURL, setActionType, updateReportsFilters } from '../../actions/reports';
import css from './Grid.css';
import TotalsCell from '../widgets/TotalsCell';
import GridService from '../../services/grid';
import {
    autoGroupColumnDef,
    getTotalsRowData,
    createRowIdGetter,
    calculatedMetricSumAggFunc,
    calculatedMetricCountAggFunc,
    calculatedMetricMaxAggFunc,
    calculatedMetricMinAggFunc,
    calculatedMetricAvgAggFunc,
    calculatedMetricFirstAggFunc,
    calculatedMetricLastAggFunc,
    AGG_OPERATORS,
} from '../../utils/grids';
import GridGeneralCellRenderer from '../widgets/GridGeneralCellRenderer';
import GridCustomHeader from '../widgets/GridCustomHeader';
import { CREATIVE_IMAGE } from '../../utils/reportsConsts';
import { isObjectEmpty } from '../../utils/general';
import { useFetchTotals } from '../../reports/totals';
import { extractFromUrl, ReportUrlParams } from '../../utils/url';

const COLUMN_TYPES = {
    METRIC: 'metric',
    DIMENSION: 'dimension',
};

const DEFAULT_COL_DEF = {
    minWidth: 50,
    floatingFilter: true,
};

const AG_GRID_PIVOT_ROW_PREFIX = /^ag-Grid-AutoColumn-/;

const DEFAULT_COLUMN_DEFS = [];
const DEFAULT_ROW_DATA = [];
const DEFAULT_COLUMN_TYPES = {};
const DEFAULT_POST_PROCESS_FILTERS_UI = {};
const DEFAULT_SET_EXPORT_PARAMS = () => {};

const Grid = props => {
    const {
        id = 'generic_grid',
        columnDefs = DEFAULT_COLUMN_DEFS,
        reportType = null,
        pivotTable: pivotTableProp,
        pivotMode: pivotModeProp = false,
        pinnedColumns: pinnedColumnsProp,
        columnOrder: columnOrderProp,
        rowData = DEFAULT_ROW_DATA,
        totalsData: initialTotalsData,
        className = '',
        children = null,
        hidePivotHeaders = false,
        hideColumnsSideBar = false,
        exportParams = null,
        setExportParams = DEFAULT_SET_EXPORT_PARAMS,
        columnTypes = DEFAULT_COLUMN_TYPES,
        onMoveColumn,
        onTableFilterChanged,
        postProcessFiltersUI = DEFAULT_POST_PROCESS_FILTERS_UI,
        disableUrlSync = false,
        queryObject,
    } = props;

    const store = useStore();
    const dispatch = useDispatch();

    const onReady = () => dispatch(gridReady(reportType));
    const onUpdateQueryURL = useCallback(
        (pivotState, columnOrder, pinnedColumns) =>
            dispatch(setActionType(updateQueryURL(reportType, pivotState, columnOrder, pinnedColumns), reportType)),
        [dispatch, reportType]
    );

    const { totalsData, isLoading, fetchTotals } = useFetchTotals(reportType, id, queryObject);

    const [visible, setVisible] = useState(false);
    const [pivotMode, setPivotMode] = useState(pivotModeProp);

    const gridApiRef = useRef(null);
    const gridServiceRef = useRef(null);

    // Track isLoading in a ref to avoid re-rendering grid column definitions
    const isTotalsLoadingRef = useRef(isLoading);
    isTotalsLoadingRef.current = isLoading;

    // Pass isLoading to createRowIdGetter to handle the totals row re-rendering
    const getRowId = useMemo(() => createRowIdGetter(columnDefs, isLoading), [columnDefs, isLoading]);

    const pinnedTopRowData = useMemo(() => {
        if (!rowData?.length) return [];

        if (gridApiRef.current?.getDisplayedRowCount() === 0) return [];

        const totals = getTotalsRowData(totalsData || initialTotalsData || {});
        return totals.length > 0 ? totals : isLoading ? [{ __isTotalsRow: true }] : [];
    }, [initialTotalsData, totalsData, isLoading, rowData]);

    const normalizeColumnId = useCallback(colId => colId.replace(AG_GRID_PIVOT_ROW_PREFIX, ''), []);

    const gridColumnDefs = useMemo(() => {
        const urlColumnOrder = extractFromUrl(ReportUrlParams.COLUMN_ORDER);

        const columnOrder =
            (urlColumnOrder?.length && urlColumnOrder) ||
            (columnOrderProp?.length && columnOrderProp) ||
            columnDefs.map(({ colId }) => colId);

        const orderedColumns = [
            ...new Set(
                columnOrder
                    .map(colId => columnDefs.find(col => normalizeColumnId(col.colId) === normalizeColumnId(colId)))
                    .filter(Boolean)
            ),
        ];

        // Ensure new fields not in column order are still shown
        const normalizedColumnOrder = new Set(columnOrder.map(normalizeColumnId));
        const { newDimensions, newNonDimensions } = columnDefs.reduce(
            (acc, col) => {
                if (normalizedColumnOrder.has(normalizeColumnId(col.colId))) {
                    return acc;
                }

                if (col.type === COLUMN_TYPES.DIMENSION) {
                    acc.newDimensions.push(col);
                } else {
                    acc.newNonDimensions.push(col);
                }

                return acc;
            },
            { newDimensions: [], newNonDimensions: [] }
        );

        let allColumns;
        if (newDimensions.length || newNonDimensions.length) {
            const firstNonDimensionIndex = orderedColumns.findIndex(col => col.type !== COLUMN_TYPES.DIMENSION);
            if (firstNonDimensionIndex >= 0) {
                allColumns = [
                    ...orderedColumns.slice(0, firstNonDimensionIndex),
                    ...newDimensions,
                    ...orderedColumns.slice(firstNonDimensionIndex),
                    ...newNonDimensions,
                ];
            } else {
                allColumns = [...orderedColumns, ...newDimensions, ...newNonDimensions];
            }
        } else {
            allColumns = orderedColumns;
        }

        return allColumns.map(cell => ({
            ...cell,
            cellRenderer: cellProps => {
                const isPinnedTop = cellProps.node.rowPinned === 'top';
                const CellComponent = isPinnedTop ? TotalsCell : GridGeneralCellRenderer;

                return (
                    <Provider store={store}>
                        <CellComponent
                            {...cellProps}
                            reportType={reportType}
                            isLoading={isPinnedTop && isTotalsLoadingRef.current}
                        />
                    </Provider>
                );
            },
        }));
    }, [columnDefs, columnOrderProp, store, reportType, normalizeColumnId]);

    const filterValidColumns = useCallback(
        fields => fields?.filter(field => columnDefs.some(cd => cd.field === field)) || [],
        [columnDefs]
    );

    const isPivotWithColumns = useCallback(api => pivotMode && api?.getPivotColumns()?.length, [pivotMode]);

    const updateTotals = useCallback(
        filterModel => {
            // Totals calculation in the BE for pivot mode with columns is not supported.
            if (isPivotWithColumns(gridApiRef.current)) return;

            fetchTotals(columnDefs, filterModel || {});
        },
        [fetchTotals, isPivotWithColumns, columnDefs]
    );

    const applyPivotTableSettings = useCallback(() => {
        if (!gridApiRef.current || !pivotMode) return;

        const defaultSettings = {
            rowGroup: columnDefs.filter(colDef => colDef.type === COLUMN_TYPES.DIMENSION).map(col => col.field),
            values: columnDefs.filter(colDef => colDef.type === COLUMN_TYPES.METRIC).map(col => col.field),
            columns: [],
        };

        const urlPivotTable = extractFromUrl(ReportUrlParams.PIVOT_TABLE);

        const pivotTable =
            (!isObjectEmpty(urlPivotTable) && urlPivotTable) ||
            (!isObjectEmpty(pivotTableProp) && pivotTableProp) ||
            {};

        const settings = { ...defaultSettings, ...pivotTable };

        gridApiRef.current.setRowGroupColumns(filterValidColumns(settings.rowGroup));
        gridApiRef.current.setPivotColumns(filterValidColumns(settings.columns));
        gridApiRef.current.setValueColumns(filterValidColumns(settings.values));
    }, [pivotMode, pivotTableProp, filterValidColumns, columnDefs]);

    const applyPinnedColumns = useCallback(() => {
        const api = gridApiRef.current;
        if (!api) return;

        const urlPinnedColumns = extractFromUrl(ReportUrlParams.PINNED_COLUMNS);
        const pinned =
            (!isObjectEmpty(urlPinnedColumns) && urlPinnedColumns) ||
            (!isObjectEmpty(pinnedColumnsProp) && pinnedColumnsProp) ||
            {};

        if (!pinned) return;

        const { left = [], right = [] } = pinned;

        const columns = api.getAllDisplayedColumns?.() || [];
        const findColId = colId => columns.find(col => col.getColId() === colId)?.getColId();

        const state = [
            ...left.map(colId => ({ colId: findColId(colId), pinned: 'left' })),
            ...right.map(colId => ({ colId: findColId(colId), pinned: 'right' })),
        ].filter(({ colId }) => colId);

        if (!state.length) return;

        api.applyColumnState({ state, defaultState: { pinned: null } });
    }, [pinnedColumnsProp]);

    const applyFiltersFromURL = useCallback(() => {
        if (isObjectEmpty(postProcessFiltersUI)) return;

        const filterModel = Object.fromEntries(Object.entries(postProcessFiltersUI));
        gridApiRef.current.setFilterModel(filterModel);
    }, [postProcessFiltersUI]);

    const onGridReady = useCallback(
        params => {
            gridApiRef.current = params.api;
            gridServiceRef.current = new GridService(id, params.api);

            applyPivotTableSettings();
            applyPinnedColumns();
            applyFiltersFromURL();
            gridApiRef.current.autoSizeAllColumns();

            setTimeout(() => setVisible(true), 0);

            onReady();
        },
        [id, applyPivotTableSettings, applyFiltersFromURL, applyPinnedColumns]
    );

    const updateURLQuery = useCallback(
        ({ api }) => {
            if (disableUrlSync) return;

            const pivotState =
                !api || api.isDestroyed() || !pivotMode
                    ? null
                    : {
                          rowGroup: api.getRowGroupColumns().map(col => col.getColId()),
                          columns: api.getPivotColumns().map(col => col.getColId()),
                          values: api.getValueColumns().map(col => col.getColId()),
                      };

            const columns = api.getAllDisplayedColumns?.() || [];

            const getPinnedColumns = direction =>
                columns.filter(col => col.getPinned?.() === direction).map(col => col.getColId());

            const left = getPinnedColumns('left');
            const right = getPinnedColumns('right');

            const pinnedColumns = left.length || right.length ? { left, right } : null;

            // Can't save pivot order with columns because it must be stored in the URL in a flattened format
            const columnOrder = !isPivotWithColumns(api)
                ? api.getAllDisplayedColumns().map(col => normalizeColumnId(col.getColId()))
                : null;

            onUpdateQueryURL(pivotState, columnOrder, pinnedColumns);
        },
        [pivotMode, onUpdateQueryURL, normalizeColumnId, disableUrlSync, isPivotWithColumns]
    );

    const onColumnMoved = useCallback(
        e => {
            const { finished, api, column, toIndex } = e;
            if (!finished || !api) return;

            if (pivotMode && column) {
                const normalizedMovedColId = normalizeColumnId(column.getColId());
                const rowGroupColumns = api.getRowGroupColumns();
                const oldIndex = rowGroupColumns.findIndex(col => col.getColId() === normalizedMovedColId);

                if (oldIndex !== -1) {
                    const newRowGroupIndex = Math.min(toIndex, rowGroupColumns.length);

                    if (oldIndex !== newRowGroupIndex) {
                        api.moveRowGroupColumn(oldIndex, newRowGroupIndex);
                    }
                }
            }

            if (!isPivotWithColumns(api)) {
                const newOrder = api.getAllDisplayedColumns().map(col => normalizeColumnId(col.getColId()));
                onMoveColumn?.({ columnOrder: newOrder });
            }

            updateURLQuery(e);
        },
        [onMoveColumn, updateURLQuery, normalizeColumnId, isPivotWithColumns, pivotMode]
    );

    const onFilterChanged = useCallback(() => {
        const filterModel = gridApiRef.current.getFilterModel();
        updateTotals(filterModel);
        onTableFilterChanged?.(filterModel);
        dispatch(updateReportsFilters(reportType, filterModel, columnDefs));
    }, [updateTotals, onTableFilterChanged, dispatch, reportType, columnDefs]);

    const closeToolPanel = useCallback(() => gridApiRef.current.openToolPanel(null), []);

    const onColumnPivotModeChanged = useCallback(() => {
        closeToolPanel();
        setPivotMode(!pivotMode);
    }, [pivotMode, closeToolPanel]);

    useEffect(() => {
        if (!exportParams) return;

        gridApiRef.current.exportDataAsCsv(exportParams);
        setExportParams(null);
    }, [exportParams, setExportParams]);

    useEffect(() => (pivotMode ? applyPivotTableSettings() : gridApiRef.current?.setRowGroupColumns([])), [
        pivotMode,
        applyPivotTableSettings,
    ]);

    const sideBar = useMemo(() => {
        const toolPanels = [];

        if (!hideColumnsSideBar) {
            const columnsPanel = {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                    suppressRowGroups: !pivotMode,
                    suppressPivots: !pivotMode,
                    suppressValues: !pivotMode,
                    suppressSideButtons: false,
                    suppressColumnSelectAll: false,
                    suppressColumnExpandAll: false,
                },
            };

            toolPanels.push(columnsPanel);
        }

        toolPanels.push({
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
            toolPanelParams: {
                suppressExpandAll: false,
                suppressFilterSearch: false,
                suppressSyncLayoutWithGrid: false,
            },
        });

        return { toolPanels };
    }, [hideColumnsSideBar, pivotMode]);

    const includeAssets = useMemo(() => gridColumnDefs?.find(({ field }) => field === CREATIVE_IMAGE), [
        gridColumnDefs,
    ]);

    if (!rowData?.length) return null;

    const pivotDisplayMode = pivotMode && !hidePivotHeaders ? 'always' : 'never';

    // Register custom aggregation functions for calculated metrics using standard AgGrid names
    // This overrides the built-in functions to handle calculated metric objects correctly
    const aggFuncs = useMemo(
        () => ({
            [AGG_OPERATORS.SUM]: calculatedMetricSumAggFunc,
            [AGG_OPERATORS.COUNT]: calculatedMetricCountAggFunc,
            [AGG_OPERATORS.MAX]: calculatedMetricMaxAggFunc,
            [AGG_OPERATORS.MIN]: calculatedMetricMinAggFunc,
            [AGG_OPERATORS.AVG]: calculatedMetricAvgAggFunc,
            [AGG_OPERATORS.FIRST]: calculatedMetricFirstAggFunc,
            [AGG_OPERATORS.LAST]: calculatedMetricLastAggFunc,
        }),
        []
    );

    return (
        <div className={classNames(className, css.gridWithChart, { [css.visible]: visible })}>
            {children}
            <div className={classNames(css.gridContainer)}>
                <AgGridReact
                    className="ag-theme-quartz"
                    modules={[AllEnterpriseModule]}
                    rowData={rowData}
                    getRowId={getRowId}
                    columnDefs={gridColumnDefs}
                    autoGroupColumnDef={autoGroupColumnDef}
                    defaultColDef={DEFAULT_COL_DEF}
                    suppressDragLeaveHidesColumns
                    suppressAggFuncInHeader
                    groupHideOpenParents
                    groupDefaultExpanded={-1}
                    pivotDefaultExpanded={-1}
                    suppressContextMenu
                    allowDragFromColumnsToolPanel
                    sideBar={sideBar}
                    columnTypes={columnTypes}
                    pinnedTopRowData={pinnedTopRowData}
                    pivotMode={pivotMode}
                    rowGroupPanelShow={pivotDisplayMode}
                    pivotPanelShow={pivotDisplayMode}
                    components={{ GridCustomHeader }}
                    aggFuncs={aggFuncs}
                    onGridReady={onGridReady}
                    rowHeight={includeAssets ? 48 : 40}
                    onColumnMoved={onColumnMoved}
                    onFilterChanged={onFilterChanged}
                    onRowClicked={closeToolPanel}
                    onColumnPivotModeChanged={onColumnPivotModeChanged}
                    onColumnRowGroupChanged={updateURLQuery}
                    onColumnPivotChanged={updateURLQuery}
                    onColumnValueChanged={updateURLQuery}
                    onColumnPinned={updateURLQuery}
                    onColumnVisible={updateURLQuery}
                    animateRows={false}
                    rowBuffer={10}
                />
            </div>
        </div>
    );
};

Grid.propTypes = {
    columnDefs: PropTypes.arrayOf(PropTypes.object),
    rowData: PropTypes.arrayOf(PropTypes.object),
    totalsData: PropTypes.arrayOf(PropTypes.object),
    columnTypes: PropTypes.objectOf(PropTypes.any),
    id: PropTypes.string,
    children: PropTypes.element,
    reportType: PropTypes.string,
    pivotMode: PropTypes.bool,
    pivotTable: PropTypes.objectOf(PropTypes.any),
    pinnedColumns: PropTypes.shape({
        left: PropTypes.arrayOf(PropTypes.string),
        right: PropTypes.arrayOf(PropTypes.string),
    }),
    columnOrder: PropTypes.arrayOf(PropTypes.string),
    className: PropTypes.string,
    hidePivotHeaders: PropTypes.bool,
    hideColumnsSideBar: PropTypes.bool,
    exportParams: PropTypes.objectOf(PropTypes.any),
    setExportParams: PropTypes.func,
    onMoveColumn: PropTypes.func,
    onTableFilterChanged: PropTypes.func,
    postProcessFiltersUI: PropTypes.objectOf(PropTypes.any),
    disableUrlSync: PropTypes.bool,
    queryObject: PropTypes.objectOf(PropTypes.any),
};

export default memo(Grid);
